import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '@/components/MobileLayout';
import TopBar from '@/components/TopBar';
import MediaPicker from '@/components/MediaPicker';
import { api } from '@/lib/api';
import { RefreshCw, Eye, Send, Loader2, Newspaper, Search, X, Link2 } from 'lucide-react';
import { toast } from 'sonner';

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'à l\'instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

function exactTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function Feed() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState('mixed'); // 'mixed' | 'date'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingTopic, setPendingTopic] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [submittingUrl, setSubmittingUrl] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async (silent = false, force = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/topics', force ? { params: { refresh: true } } : undefined);
      setTopics(data);
    } catch (e) {
      toast.error('Impossible de charger les actualités');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load(true, true);
    toast.success('Feed actualisé');
  };

  const onPublish = async (topic) => {
    // Check for recent duplicate before opening picker
    try {
      const { data } = await api.post('/articles/check-duplicate', {
        topic_url: topic.url,
        topic_title: topic.title,
      });
      if (data?.duplicate && data?.match) {
        const ok = window.confirm(
          `Un article similaire a déjà été ${data.match.status === 'published' ? 'publié' : 'généré'} il y a moins de 24h:\n\n"${data.match.title}"\n\nGénérer quand même ?`
        );
        if (!ok) return;
      }
    } catch (e) { /* silent — don't block on check failure */ }
    setPendingTopic(topic);
    setPickerOpen(true);
  };

  const onMediaSelected = (media, extraInstruction) => {
    setPickerOpen(false);
    if (!pendingTopic) return;
    sessionStorage.setItem('autoradko_topic', JSON.stringify(pendingTopic));
    sessionStorage.setItem('autoradko_media', JSON.stringify(media));
    if (extraInstruction) {
      sessionStorage.setItem('autoradko_extra_instruction', extraInstruction);
    } else {
      sessionStorage.removeItem('autoradko_extra_instruction');
    }
    nav('/generate');
  };

  const onView = (topic) => {
    sessionStorage.setItem('autoradko_topic', JSON.stringify(topic));
    nav('/reader');
  };

  const submitUrl = async () => {
    const u = urlInput.trim();
    if (!u) return;
    setSubmittingUrl(true);
    try {
      const { data } = await api.post('/topics/from-url', { url: u });
      setPendingTopic(data);
      setPickerOpen(true);
      setUrlInput('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'URL invalide');
    } finally { setSubmittingUrl(false); }
  };

  const filteredTopics = useMemo(() => {
    let list = topics;
    if (categoryFilter !== 'all') {
      // Special filters: 'outremer' / 'afrique' filter by REGION, others by category
      if (categoryFilter === 'outremer' || categoryFilter === 'afrique') {
        list = list.filter((t) => t.region === categoryFilter);
      } else {
        list = list.filter((t) => t.category === categoryFilter);
      }
    }
    if (sortMode === 'date') {
      list = [...list].sort((a, b) => (b.published_at || '').localeCompare(a.published_at || ''));
    }
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((t) =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.summary || '').toLowerCase().includes(q) ||
      (t.source || '').toLowerCase().includes(q)
    );
  }, [topics, search, sortMode, categoryFilter]);

  // Counts per category and per region
  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const t of topics) {
      if (t.category) counts[t.category] = (counts[t.category] || 0) + 1;
    }
    return counts;
  }, [topics]);

  const regionCounts = useMemo(() => {
    const counts = { outremer: 0, afrique: 0 };
    for (const t of topics) {
      if (t.region === 'outremer') counts.outremer++;
      else if (t.region === 'afrique') counts.afrique++;
    }
    return counts;
  }, [topics]);

  return (
    <MobileLayout>
      <TopBar />

      <div className="px-5 pt-5 pb-3">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une actualité…"
            className="w-full pl-11 pr-10 py-3 bg-white border-2 border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none text-sm shadow-sm transition"
            data-testid="feed-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              data-testid="btn-clear-search"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
              aria-label="Effacer"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Black URL bar — paste an article URL to publish it directly */}
        <div className="mt-3 relative">
          <Link2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none" />
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitUrl(); } }}
            placeholder="Coller un lien d'article…"
            className="w-full pl-11 pr-28 py-3 bg-slate-900 text-white placeholder-white/50 border-2 border-slate-900 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm shadow-md transition"
            data-testid="feed-url-input"
          />
          <button
            onClick={submitUrl}
            disabled={submittingUrl || !urlInput.trim()}
            data-testid="btn-submit-url"
            className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl flex items-center gap-1 transition"
          >
            {submittingUrl ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Publier
          </button>
        </div>
      </div>

      <div className="px-5 pt-1 pb-2 flex items-center justify-between">
        <h1 className="text-lg font-bold text-slate-900" data-testid="feed-title">Dernières actualités</h1>
        <button
          onClick={refresh}
          disabled={refreshing}
          data-testid="btn-refresh-feed"
          className="p-2 rounded-full hover:bg-blue-50 text-blue-600 active:scale-95 transition disabled:opacity-50"
          aria-label="Rafraîchir"
        >
          <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {!loading && topics.length > 0 && (
        <div className="px-5 pb-2 flex items-center gap-2" data-testid="sort-toggle">
          <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Tri :</span>
          <button
            onClick={() => setSortMode('mixed')}
            data-testid="btn-sort-mixed"
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${sortMode === 'mixed' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            ⚖︎ Varié
          </button>
          <button
            onClick={() => setSortMode('date')}
            data-testid="btn-sort-date"
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${sortMode === 'date' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            🕒 Récent
          </button>
        </div>
      )}

      {!loading && topics.length > 0 && (
        <div className="px-5 pb-2 overflow-x-auto" data-testid="category-chips">
          <div className="flex gap-2 pb-1" style={{ width: 'max-content' }}>
            {[
              { key: 'all', label: 'Tous', emoji: '📰', kind: 'default' },
              { key: 'outremer', label: 'Outre-mer', emoji: '🌴', kind: 'om' },
              { key: 'afrique', label: 'Afrique', emoji: '🌍', kind: 'af' },
              { key: 'politique', label: 'Politique', emoji: '🏛️', kind: 'default' },
              { key: 'international', label: 'International', emoji: '🌐', kind: 'default' },
              { key: 'société', label: 'Société', emoji: '👥', kind: 'default' },
              { key: 'économie', label: 'Économie', emoji: '💼', kind: 'default' },
              { key: 'sport', label: 'Sport', emoji: '⚽', kind: 'default' },
              { key: 'culture', label: 'Culture', emoji: '🎭', kind: 'default' },
              { key: 'tech', label: 'Tech', emoji: '💻', kind: 'default' },
              { key: 'environnement', label: 'Environnement', emoji: '🌱', kind: 'default' },
            ].map((c) => {
              let count;
              if (c.key === 'all') count = topics.length;
              else if (c.key === 'outremer') count = regionCounts.outremer;
              else if (c.key === 'afrique') count = regionCounts.afrique;
              else count = categoryCounts[c.key] || 0;
              const isActive = categoryFilter === c.key;
              if (c.key !== 'all' && count === 0) return null;

              // Color scheme per chip kind
              let activeCls = 'bg-blue-600 text-white shadow-sm';
              let badgeActiveCls = 'bg-white/20 text-white';
              if (c.kind === 'om') {
                activeCls = 'bg-green-600 text-white shadow-sm';
                badgeActiveCls = 'bg-white/20 text-white';
              } else if (c.kind === 'af') {
                activeCls = 'bg-red-600 text-white shadow-sm';
                badgeActiveCls = 'bg-white/20 text-white';
              }
              const inactiveCls = c.kind === 'om'
                ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                : c.kind === 'af'
                  ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200';

              return (
                <button
                  key={c.key}
                  onClick={() => setCategoryFilter(c.key)}
                  data-testid={`btn-category-${c.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition flex items-center gap-1 ${isActive ? activeCls : inactiveCls}`}
                >
                  <span>{c.emoji}</span>
                  {c.label}
                  <span className={`text-[10px] font-bold px-1.5 rounded-full ${isActive ? badgeActiveCls : 'bg-white/60 text-slate-700'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-5 pt-2 pb-6 space-y-4" data-testid="feed-list">
        {loading && (
          <div className="py-20 text-center text-slate-500">
            <Loader2 className="mx-auto animate-spin mb-2" />
            <div className="text-sm">Chargement des actualités…</div>
          </div>
        )}

        {!loading && topics.length === 0 && (
          <div className="py-16 text-center" data-testid="feed-empty">
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-3">
              <Newspaper className="text-blue-600" />
            </div>
            <p className="text-slate-700 font-semibold">Aucune actualité pour l'instant</p>
            <p className="text-sm text-slate-500 mt-1">Ajoutez des flux RSS dans les réglages.</p>
            <button
              onClick={() => nav('/settings/feeds')}
              className="mt-4 bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-blue-700 active:scale-95 transition"
              data-testid="btn-go-feeds"
            >
              Gérer les flux RSS
            </button>
          </div>
        )}

        {!loading && topics.length > 0 && filteredTopics.length === 0 && (
          <div className="py-10 text-center text-slate-500 text-sm" data-testid="feed-no-match">
            Aucun résultat pour « {search} »
          </div>
        )}

        {!loading && topics.length > 0 && filteredTopics.length === 0 && (
          <div className="py-12 text-center" data-testid="feed-empty-filter">
            <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-2">
              <Newspaper className="text-slate-400" />
            </div>
            <p className="text-slate-700 font-semibold">Aucune actualité dans cette catégorie</p>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              {search ? 'Essayez d\'effacer la recherche.' : 'Essayez une autre catégorie.'}
            </p>
            <button
              onClick={() => { setCategoryFilter('all'); setSearch(''); }}
              className="bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-xl"
              data-testid="btn-show-all"
            >
              Voir toutes les actus
            </button>
          </div>
        )}

        {!loading && filteredTopics.map((t) => {
          // Color the card by region: green for outre-mer, red for Africa
          const isOM = t.region === 'outremer';
          const isAF = t.region === 'afrique';
          const cardClass = isOM
            ? 'bg-green-50 border-green-300'
            : isAF
              ? 'bg-red-50 border-red-300'
              : 'bg-white border-slate-200';
          return (
          <article
            key={t.id}
            className={`border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow fade-in-up ${cardClass}`}
            data-testid={`topic-card-${t.id}`}
          >
            <h2 className="text-[1.05rem] font-bold text-slate-900 leading-snug">{t.title}</h2>
            {t.summary && (
              <p className="text-sm text-slate-600 mt-2 line-clamp-3">{t.summary}</p>
            )}
            <div className="mt-3 text-xs flex items-center gap-2 flex-wrap">
              <span className="text-slate-600 font-medium">{timeAgo(t.published_at)}</span>
              {t.published_at && (
                <span className="text-slate-400 text-[11px]">
                  ({exactTime(t.published_at)})
                </span>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => onView(t)}
                data-testid={`btn-view-${t.id}`}
                className="flex-1 border-2 border-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl hover:bg-slate-50 hover:border-slate-300 active:scale-95 transition flex items-center justify-center gap-1.5"
              >
                <Eye size={16} /> Voir
              </button>
              <button
                onClick={() => onPublish(t)}
                data-testid={`btn-publish-${t.id}`}
                className="flex-1 bg-blue-600 text-white font-semibold py-2.5 rounded-xl hover:bg-blue-700 active:scale-95 transition flex items-center justify-center gap-1.5"
              >
                <Send size={16} /> Publier
              </button>
            </div>
            {/* Source badge — at the bottom of the card */}
            {(t.source || t.category) && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap" data-testid={`source-badge-${t.id}`}>
                {t.source && (
                  <>
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Publié par</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${
                      isOM ? 'bg-green-100 text-green-800 border-green-200'
                      : isAF ? 'bg-red-100 text-red-800 border-red-200'
                      : 'bg-blue-50 text-blue-700 border-blue-100'
                    }`}>
                      {t.source}
                    </span>
                  </>
                )}
                {isOM && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-green-600 text-white">
                    🌴 Outre-mer
                  </span>
                )}
                {isAF && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white">
                    🌍 Afrique
                  </span>
                )}
                {t.category && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200">
                    {t.category}
                  </span>
                )}
              </div>
            )}
          </article>
          );
        })}
      </div>
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onMediaSelected}
      />
    </MobileLayout>
  );
}
